import React, { useEffect, useState } from "react";
import { Card, Collapse, Tabs, Tag } from "antd";
import { first, groupBy, isEmpty, map, orderBy } from "lodash";
import usePayrollStore from "../../../config/stores/administration/usePayrollStore";
import PayrollForm from "./PayrollForm";
import PayrollPeriodForm from "./PayrollPeriodForm";
import PayrollDetails from "./PayrollDetails";
import PayrollListTable from "./PayrollListTable";
import AppButton from "../../../components/common/AppButton";
import { AiOutlinePlus } from "react-icons/ai";
import dayjs from "dayjs";

const Payroll = () => {
  const payrollStore = usePayrollStore();
  const { payrolls, getAllPayrolls, showPayrollDetails, currentPayrollPeriod } = payrollStore;
  const [activeKeys, setActiveKeys] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState(null);

  useEffect(() => {
    if (isEmpty(payrolls)) {
      getAllPayrolls();
    }
  }, [payrolls, getAllPayrolls]);

  const allPayrollData = payrolls?.data || payrolls || [];
  
  const payrollGroupedByYear = orderBy(
    map(
      groupBy(allPayrollData, "year"),
      (yearPayrolls, year) => {
        const payrolls = orderBy(
          map(
            groupBy(
              yearPayrolls,
              (payroll) => `${payroll.month}-${payroll.year}`
            ),
            (records) => {
              const actualPayrolls = records.filter(r => r.id !== null);
              const context = first(records);
              
              if (actualPayrolls.length === 0 && records.length > 0) {
                return {
                  payrolls: [],
                  month: dayjs().month(context.month - 1).format("MMMM"),
                  monthNo: context.month,
                  year: context.year,
                };
              }

              return {
                payrolls: actualPayrolls,
                month: dayjs().month(context.month - 1).format("MMMM"),
                monthNo: context.month,
                year: context.year,
              };
            }
          ),
          ["year", "monthNo"],
          ["desc", "desc"]
        );

        if (currentPayrollPeriod && String(currentPayrollPeriod.year) === String(year)) {
          const monthName = dayjs().month(currentPayrollPeriod.month - 1).format("MMMM");
          const periodExists = payrolls.some(
            p => p.monthNo === currentPayrollPeriod.month && p.year === currentPayrollPeriod.year
          );
          
          if (!periodExists) {
            payrolls.push({
              payrolls: [],
              month: monthName,
              monthNo: currentPayrollPeriod.month,
              year: currentPayrollPeriod.year,
            });
            payrolls.sort((a, b) => {
              if (a.year !== b.year) return b.year - a.year;
              return b.monthNo - a.monthNo;
            });
          }
        }

        return {
          year,
          payrolls,
        };
      }
    ),
    ["year"],
    ["desc"]
  );

  if (currentPayrollPeriod) {
    const yearExists = payrollGroupedByYear.some(
      y => String(y.year) === String(currentPayrollPeriod.year)
    );
    
    if (!yearExists) {
      const monthName = dayjs().month(currentPayrollPeriod.month - 1).format("MMMM");
      payrollGroupedByYear.push({
        year: currentPayrollPeriod.year,
        payrolls: [{
          payrolls: [],
          month: monthName,
          monthNo: currentPayrollPeriod.month,
          year: currentPayrollPeriod.year,
        }],
      });
      payrollGroupedByYear.sort((a, b) => b.year - a.year);
    }
  }

  useEffect(() => {
    if (currentPayrollPeriod) {
      const { month, year } = currentPayrollPeriod;
      const monthName = dayjs().month(month - 1).format("MMMM");
      const periodKey = ` ${monthName} - ${year}`;
      
      setActiveTabKey(String(year));
      setActiveKeys([periodKey]);
    }
  }, [currentPayrollPeriod]);

  const openAddModal = () => {
    payrollStore.setCreatingPayrollPeriod(true);
  };

  return (
    <div className="container-fluid p-2">
      {showPayrollDetails ? (
        <PayrollDetails />
      ) : (
        <Card
          title="PAYROLL"
          extra={
            <AppButton
              text="CREATE PAYROLL TIME PERIOD"
              onClick={openAddModal}
              iconBefore={<AiOutlinePlus />}
            />
          }
        >
          <Tabs
            type="card"
            activeKey={activeTabKey || undefined}
            onChange={(key) => setActiveTabKey(key)}
            items={map(payrollGroupedByYear, (yearPayrolls) => ({
              label: <span>{yearPayrolls.year}</span>,
              key: String(yearPayrolls.year),
              children: (
                <Collapse
                  activeKey={activeKeys}
                  onChange={(keys) => setActiveKeys(keys)}
                  items={map(yearPayrolls.payrolls, (payroll) => ({
                    label: (
                      <span className="text-uppercase fw-bold">
                        {payroll.month} -{" "}
                        <Tag color="green">{payroll.year}</Tag>
                      </span>
                    ),
                    key: ` ${payroll.month} - ${payroll.year}`,
                    children: (
                      <PayrollListTable 
                        payrolls={payroll.payrolls}
                        month={payroll.monthNo}
                        year={payroll.year}
                      />
                    ),
                  }))}
                />
              ),
            }))}
          />
        </Card>
      )}

      <PayrollPeriodForm />
      <PayrollForm />
    </div>
  );
};

export default Payroll;


